import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import HoldingsFilters from "../components/HoldingsFilters";
import HoldingsStatementSelector from "../components/HoldingsStatementSelector";
import HoldingsPortfolioSelector from "../components/HoldingsPortfolioSelector";
import HoldingsGrid from "../components/HoldingsGrid";
import useAuthStore from "../../../store/authStore";
import * as XLSX from "xlsx";
import {
  exportTimeSeries,
  getPortfolioAttributionUIData,
  getPortfolioByClientStatement,
  getPortfolioDocumentMapping,
  getPortfolioGroupTreeData,
  getReportClassifications,
  getViews,
} from "../services/assetHoldingsService";
import "../styles/assetHoldings.css";
import { toast } from "react-toastify";

const toPrevMonthEnd = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 0);
};

const mapFamilyTreeNodes = (families = []) =>
  families.map((family) => ({
    id: String(family.familyId ?? family.FamilyId ?? ""),
    text: family.familyName ?? family.FamilyName ?? "",
    type: "Family",
    items: (family.clientStatements || family.ClientStatements || []).map((statement) => ({
      id: String(statement.clientStatementId ?? statement.ClientStatementId ?? ""),
      text:
        statement.clientStatementName
        ?? statement.ClientStatementName
        ?? statement.accountName
        ?? statement.AccountName
        ?? "",
      type: "ClientStatement",
      items: [],
    })),
  }));

const formatValue = (value, keyName) => {
  if (value == null || value === "") {
    return "-";
  }

  const key = String(keyName || "").toUpperCase();
  const numeric = Number(value);
  const isNumber = Number.isFinite(numeric);

  if (!isNumber) {
    return String(value);
  }

  const percentKeys = [
    "BEG%",
    "END%",
    "QTD",
    "ITD",
    "ATTRIBUTIONPERCENT",
    "RORRETURN",
    "YTDROR",
    "QTDROR",
    "ITDROR",
    "ITDANNROR",
    "ITDATT%",
    "QTDATT%",
    "YTDATT%",
    "MTD",
    "COMPOUNDRORDAILY",
    "CUMULATIVERORDAILY",
    "COMPOUNDROR",
    "CUMULATIVEROR",
  ];

  if (percentKeys.includes(key)) {
    return `${(numeric * 100).toFixed(2)}%`;
  }

  if (key.includes("DATE")) {
    const dt = new Date(value);
    if (Number.isFinite(dt.getTime())) {
      return dt.toLocaleDateString("en-US");
    }
    return String(value);
  }

  if (key === "BMV" || key === "EMV" || key.includes("PNL") || key.includes("TRANSACTION")) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(numeric);
  }

  return numeric;
};

const sanitizeKey = (text) => String(text || "").replace(/[^a-zA-Z0-9]/g, "");
const getValueTypeByKeyName = (keyName) => {
  const key = String(keyName || "").toUpperCase();
  const percentKeys = [
    "BEG%",
    "END%",
    "QTD",
    "ITD",
    "ATTRIBUTIONPERCENT",
    "RORRETURN",
    "YTDROR",
    "QTDROR",
    "ITDROR",
    "ITDANNROR",
    "ITDATT%",
    "QTDATT%",
    "YTDATT%",
    "MTD",
    "COMPOUNDRORDAILY",
    "CUMULATIVERORDAILY",
    "COMPOUNDROR",
    "CUMULATIVEROR",
  ];

  if (percentKeys.includes(key)) return "percent";
  if (key.includes("DATE")) return "date";
  if (key === "BMV" || key === "EMV" || key.includes("PNL") || key.includes("TRANSACTION")) return "currency";
  return "number";
};
const normalizeText = (value) => String(value || "").trim().toLowerCase();
const getPortfolioId = (p) => String(p?.PortfolioId ?? p?.portfolioId ?? p?.Id ?? p?.id ?? "");
const pick = (obj, ...keys) => {
  for (const key of keys) {
    if (obj && obj[key] !== undefined && obj[key] !== null) {
      return obj[key];
    }
  }
  return undefined;
};
const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return NaN;
  const parsed = parseFloat(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : NaN;
};
const toArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === "object") {
    if (Array.isArray(value.data)) return value.data;
    if (Array.isArray(value.items)) return value.items;
    if (Array.isArray(value.results)) return value.results;
    return [value];
  }

  return [];
};
const GROUPING_FIELD_BY_ID = {
  1: "StrategyName",
  2: "SubStrategyName",
  3: "AssetClassName",
  4: "BillableName",
};

const getGroupingFieldsFromView = (view) => {
  const groupingItems = (view?.attrGroupingItem || view?.AttrGroupingItem || [])
    .slice()
    .sort((a, b) => (a?.sortOrder ?? a?.SortOrder ?? 0) - (b?.sortOrder ?? b?.SortOrder ?? 0));

  const fields = groupingItems
    .map((item) => GROUPING_FIELD_BY_ID[item?.groupId ?? item?.GroupId])
    .filter(Boolean);

  return fields.length > 0 ? fields : [GROUPING_FIELD_BY_ID[4], GROUPING_FIELD_BY_ID[1]];
};
// Matches `AttrLevel` / `AttrLevelCombination` enum ordering in the legacy services.
// (Unknown=0, Strategy=1, SubStrategy=2, AssetClass=3, BillableStatus=4, StrategySubStrategy=5,
//  BillableStrategy=6, BillableStrategySubStrategy=7, ...)
const getTimeSeriesLevelCategoryForView = (view) => {
  const fields = new Set(getGroupingFieldsFromView(view));
  const hasBillable = fields.has("BillableName");
  const hasStrategy = fields.has("StrategyName");
  const hasSubStrategy = fields.has("SubStrategyName");
  const hasAssetClass = fields.has("AssetClassName");

  if (hasBillable && hasStrategy && hasSubStrategy) return 7;
  if (hasBillable && hasStrategy) return 6;
  if (hasStrategy && hasSubStrategy) return 5;
  if (hasBillable) return 4;
  if (hasAssetClass) return 3;
  if (hasSubStrategy) return 2;
  if (hasStrategy) return 1;
  return 0;
};

const toCsvSafe = (value) => {
  const text = String(value ?? "");
  if (text.includes(",") || text.includes("\"") || text.includes("\n")) {
    return `"${text.replace(/"/g, "\"\"")}"`;
  }
  return text;
};

const downloadTimeSeriesExcel = (rows, assetName) => {
  const aoa = [
    ["ReportDate", "RoR"],
    ...(rows || []).map((item) => {
      const reportDateRaw = pick(
        item,
        "ReportDate",
        "reportDate",
        "Date",
        "date",
        "ReturnDate",
        "returnDate"
      );
      const reportDate = reportDateRaw ? new Date(reportDateRaw) : null;

      const roR = pick(
        item,
        "RoRNullable",
        "roRNullable",
        "RoR",
        "roR",
        "rorNullable",
        "ror"
      );
      const roRValue = roR === null || roR === undefined || roR === "" ? null : Number(roR);

      return [
        reportDate && Number.isFinite(reportDate.getTime()) ? reportDate : "",
        Number.isFinite(roRValue) ? roRValue : "",
      ];
    }),
  ];

  const safeName = String(assetName || "Time-series").replace(/[\\/:*?"<>|]+/g, "-");
  const fileName = `${safeName}-Time series export.xlsx`;

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Apply basic Excel formats (dates + percent).
  for (let i = 1; i < aoa.length; i++) {
    const rowIndex = i + 1; // 1-based sheet rows
    const dateCellAddr = XLSX.utils.encode_cell({ r: rowIndex - 1, c: 0 });
    const rorCellAddr = XLSX.utils.encode_cell({ r: rowIndex - 1, c: 1 });

    const dateCell = ws[dateCellAddr];
    if (dateCell && dateCell.v instanceof Date) {
      dateCell.t = "d";
      dateCell.z = "mm/dd/yyyy";
    }

    const rorCell = ws[rorCellAddr];
    if (rorCell && typeof rorCell.v === "number" && Number.isFinite(rorCell.v)) {
      rorCell.t = "n";
      rorCell.z = "0.00%";
    }
  }

  // Increase column widths so Excel doesn't show ###### for dates/percent values.
  ws["!cols"] = [
    { wch: 16 }, // ReportDate
    { wch: 14 }, // RoR
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Time Series");
  XLSX.writeFile(wb, fileName, { cellDates: true });
};

const mapBillableDisplayName = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "advised billable") return "Advised";
  if (normalized === "advised non-billable") return "Other Performing";
  if (normalized === "non-advised") return "Non-Performing";
  return value || "";
};

const getGroupingCombinationFromView = (view) => {
  const groupingItems = (view?.attrGroupingItem || view?.AttrGroupingItem || [])
    .slice()
    .sort((a, b) => (a?.sortOrder ?? a?.SortOrder ?? 0) - (b?.sortOrder ?? b?.SortOrder ?? 0));

  const groupIds = groupingItems
    .map((item) => Number(item?.groupId ?? item?.GroupId))
    .filter((id) => Number.isFinite(id) && id > 0);

  if (groupIds.length === 0) {
    return "-4-1";
  }

  return `-${groupIds.join("-")}`;
};

const getPortfolioDedupeKey = (item) => {
  const id = getPortfolioId(item);
  if (id) return id;

  const clientStatementId = String(item?.clientStatementId ?? item?.ClientStatementId ?? "");
  const shortName = String(item?.shortName ?? item?.PortfolioShortName ?? "");
  const name = String(item?.portfolioName ?? item?.PortfolioName ?? item?.Text ?? "");
  return `${clientStatementId}|${shortName}|${name}`;
};

const mapPortfolioItem = (item) => ({
  ...item,
  PortfolioId: item?.PortfolioId ?? item?.portfolioId ?? item?.Id ?? item?.id,
  PortfolioName: item?.PortfolioName ?? item?.portfolioName ?? item?.Text ?? item?.text ?? "",
  PortfolioShortName: item?.PortfolioShortName ?? item?.shortName ?? "",
  IsPartnership: item?.IsPartnership ?? item?.isPartnership ?? item?.IsPartnerEntity ?? false,
});

const getLevelIdForField = (rowData, fieldName) => {
  if (!rowData || !fieldName) return 0;

  if (fieldName === "StrategyName") {
    return Number(pick(rowData, "strategyId", "StrategyId")) || 0;
  }
  if (fieldName === "SubStrategyName") {
    return Number(pick(rowData, "subStrategyId", "SubStrategyId")) || 0;
  }
  if (fieldName === "AssetClassName") {
    return Number(pick(rowData, "assetClassId", "AssetClassId")) || 0;
  }
  if (fieldName === "BillableName") {
    return Number(pick(rowData, "billableSortOrder", "BillableSortOrder", "billableId", "BillableId")) || 0;
  }
  return 0;
};

const shouldIncludeFundByHideZero = (row, stats, attrs, hideZero) => {
  if (!hideZero) {
    return true;
  }

  const hasStatBmvOrEmv = stats.some((s, index) => {
    const key = String(s.keyName ?? s.KeyName ?? "").toUpperCase();
    if (key !== "BMV" && key !== "EMV") {
      return false;
    }
    const raw = row._rawStats?.[index];
    const parsed = toNumber(raw);
    return Number.isFinite(parsed) && parsed !== 0;
  });

  if (hasStatBmvOrEmv) {
    return true;
  }

  return attrs.some((attr, index) => {
    const key = String(attr.keyName ?? attr.KeyName ?? "").toUpperCase();
    if (key !== "BMV" && key !== "EMV") {
      return false;
    }

    const monthly = row._rawOtherAttrs?.[index] || [];
    return monthly.some((item) => {
      const parsed = toNumber(item);
      return Number.isFinite(parsed) && parsed !== 0;
    });
  });
};

const buildGridColumns = ({ selectedPortfoliosCount, view, reportDate, groupingFields }) => {
  const viewStats = view?.attrStatisticsItem || view?.AttrStatisticsItem || [];
  const stats = viewStats.filter((x) => (x.listType ?? x.ListType) === "S");
  const attrs = viewStats.filter((x) => (x.listType ?? x.ListType) === "A");

  const groupingColumns = (groupingFields || []).map((field) => ({
    field,
    title: field,
    hide: true,
    rowGroup: true,
  }));

  const columns = [
    ...groupingColumns,
    { field: "fund", title: "Security", width: 330, hide: true },
    { field: "BasketShortName", title: "Short Name", width: 120, pinned: "left" },
  ];

  if (attrs.length > 0) {
    const monthTitle = reportDate.toLocaleString("en-US", {
      month: "long",
      year: "numeric",
    });

    columns.push({
      title: monthTitle,
      columns: attrs.map((attr) => ({
        field: `${sanitizeKey(attr.keyName ?? attr.KeyName)}_0`,
        title: attr.friendlyName ?? attr.FriendlyName,
        width: 120,
        keyName: attr.keyName ?? attr.KeyName,
        valueType: getValueTypeByKeyName(attr.keyName ?? attr.KeyName),
        isNumeric: getValueTypeByKeyName(attr.keyName ?? attr.KeyName) !== "date",
      })),
    });
  }

  stats.forEach((stat, index) => {
    columns.push({
      field: `year${index}`,
      title: stat.friendlyName ?? stat.FriendlyName,
      width: 120,
      keyName: stat.keyName ?? stat.KeyName,
      valueType: getValueTypeByKeyName(stat.keyName ?? stat.KeyName),
      isNumeric: getValueTypeByKeyName(stat.keyName ?? stat.KeyName) !== "date",
    });
  });

  return { columns, stats, attrs };
};

const buildRunStatList = (portfolioIds, reportDate) => {
  const endDate = reportDate instanceof Date ? reportDate : new Date(reportDate);
  const safeEndDate = Number.isFinite(endDate.getTime()) ? endDate : new Date();
  const startDate = new Date(safeEndDate.getFullYear(), safeEndDate.getMonth(), 1);

  return portfolioIds.map((id) => ({
    PortfolioId: Number(id),
    PortfolioName: "",
    ShortName: null,
    Description: null,
    StartDate: startDate.toISOString(),
    EndDate: safeEndDate.toISOString(),
    EntityList: null,
    PortfolioLevel: null,
    StatsList: [
      { FriendlyName: "BMV", KeyName: "BMV", EnhancedType: 0, CumulativePeriods: true },
      { FriendlyName: "EMV", KeyName: "EMV", EnhancedType: 0, CumulativePeriods: true },
      { FriendlyName: "End %", KeyName: "End%", EnhancedType: 5, CumulativePeriods: true },
      { FriendlyName: "MTD TWR", KeyName: "MTD", EnhancedType: 0, CumulativePeriods: true },
      { FriendlyName: "QTD TWR", KeyName: "QTD", EnhancedType: 0, CumulativePeriods: true },
      { FriendlyName: "YTD TWR", KeyName: "CompoundRORDaily", EnhancedType: 0, CumulativePeriods: true },
      {
        FriendlyName: "TWR (Last 3 Years)",
        KeyName: "CompoundRORDaily",
        EnhancedType: 2,
        Years: 3,
        CoreValue: 3,
        CumulativePeriods: true,
      },
      {
        FriendlyName: "TWR (Last 5 Years)",
        KeyName: "CompoundRORDaily",
        EnhancedType: 2,
        Years: 5,
        CoreValue: 5,
        CumulativePeriods: true,
      },
      { FriendlyName: "ITD TWR (A)", KeyName: "ITD", EnhancedType: 0, CumulativePeriods: true },
      { FriendlyName: "Inception Date", KeyName: "InceptionDate", EnhancedType: 0, CumulativePeriods: true },
    ],
    StartDateType: 0,
    EndDateType: 1,
    IsStoreData: false,
    LevelCombo: 7,
    PortfolioTypeId: 0,
    UseHistory: false,
    CalculateNonPerf: false,
    IsConsolidated: false,
    WriteReturns: false,
    PartnershipId: 0,
    IsPartnership: false,
    BackingPortfolioId: 0,
    IsFlashSummary: false,
    UseInvClassification: false,
    PartnerCalculate: false,
    RunWeights: true,
    SupressLog: false,
    DoLookThrough: false,
    IsPPLI: false,
    PPLICombine: false,
    ClientStatementId: 0,
    FLEntities: [],
    CPEntities: [],
  }));
};

const AssetHoldingsPage = () => {
  const params = useParams();
  const [reportDate, setReportDate] = useState(toPrevMonthEnd());
  const [reportClassifications, setReportClassifications] = useState([]);
  const [selectedReportClassification, setSelectedReportClassification] = useState(null);
  const [views, setViews] = useState([]);
  const [selectedView, setSelectedView] = useState(null);

  const [treeData, setTreeData] = useState([]);
  const [checkedNodeIds, setCheckedNodeIds] = useState(new Set());
  const authUserId = useAuthStore((state) => Number(state.user) || 0);
  const authRoleId = useAuthStore((state) => Number(state.role) || 0);
  const authToken = useAuthStore((state) => state.token || "");

  const contactId = Number(atob(params.contactId)) || Number(authUserId)

  const [selectedPortfolios, setSelectedPortfolios] = useState([]);
  const [selectedPortfolioIds, setSelectedPortfolioIds] = useState(new Set());

  const [hideZero, setHideZero] = useState(true);
  const [loading, setLoading] = useState(true);
  const [gridColumns, setGridColumns] = useState([
    { field: "Col1", title: "|", width: 120 },
    { field: "Col2", title: "|", width: 120 },
    { field: "Col3", title: "|", width: 120 },
  ]);
  const [gridData, setGridData] = useState([]);
  const initialGridLoadedRef = useRef(false);
 
  useEffect(()=>{
    const contactName = localStorage.getItem("CN")
    document.title = `Asset Holdings - ${contactName || ""}`;
  }, [])


  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [classificationData, viewData] = await Promise.all([
          getReportClassifications(),
          getViews(),
        ]);

        setReportClassifications(classificationData);
        setViews(viewData);

        if (classificationData.length > 0) {
          const defaultClassification = classificationData.find(
            (item) => normalizeText(item.reportClassification ?? item.ReportClassification) === "strategy/sub-strategy"
          ) || classificationData[0];
          setSelectedReportClassification(defaultClassification);
        }

        if (viewData.length > 0) {
          const defaultView = viewData.find((item) => normalizeText(item.viewName ?? item.ViewName) === "holdings")
            || viewData.find((item) => item.isDefault ?? item.IsDefault)
            || viewData[0];
          setSelectedView(defaultView);
        }
      } catch (error) {
        console.error("Failed to initialize holdings dropdowns", error);
      }
    };

    loadDropdownData();
  }, []);

  useEffect(() => {
    const loadTree = async () => {
      try {
        const response = await getPortfolioGroupTreeData(contactId);
        const mappedTree = mapFamilyTreeNodes(Array.isArray(response) ? response : []);
        setTreeData(mappedTree);

        // Match MVC behavior: default-select first node from the first family group.
        const firstFamily = mappedTree[0];
        const firstNode = firstFamily?.items?.[0];
        if (firstNode?.id) {
          const initialChecked = new Set([String(firstNode.id)]);
          if (firstFamily?.id) {
            initialChecked.add(String(firstFamily.id));
          }
          setCheckedNodeIds(initialChecked);
        }
      } catch (error) {
        console.error("Failed to load portfolio tree", error);
      }
    };

    loadTree();
  }, [contactId]);

  useEffect(() => {
    const loadPortfoliosFromTreeSelection = async () => {
      const selectedClientStatements = [];

      const walk = (nodes) => {
        nodes.forEach((node) => {
          if (checkedNodeIds.has(String(node.id)) && node.type === "ClientStatement") {
            selectedClientStatements.push(String(node.id));
          }

          if (node.items?.length) {
            walk(node.items);
          }
        });
      };

      walk(treeData);

      if (selectedClientStatements.length === 0) {
        setSelectedPortfolios([]);
        setSelectedPortfolioIds(new Set());
        return;
      }

      try {
        const allPortfolioResponses = await Promise.all(
          selectedClientStatements.map((id) => getPortfolioByClientStatement(id))
        );

        const merged = allPortfolioResponses
          .flatMap((responseItem) => toArray(responseItem))
          .map(mapPortfolioItem);
        const deduplicated = Array.from(
          new Map(merged.map((item) => [getPortfolioDedupeKey(item), item])).values()
        );

        setSelectedPortfolios(deduplicated);
        setSelectedPortfolioIds(new Set(deduplicated.map((x) => getPortfolioId(x))));
      } catch (error) {
        console.error("Failed to load selected portfolios", error);
      }
    };

    loadPortfoliosFromTreeSelection();
  }, [checkedNodeIds, treeData]);

  const handleStatementSelectionChange = useCallback((statementIds) => {
    const selectedStatements = new Set((statementIds || []).map((id) => String(id)));
    const nextCheckedIds = new Set(selectedStatements);

    treeData.forEach((family) => {
      const familyStatements = (family?.items || []).map((statement) => String(statement.id));
      const hasSelectedStatement = familyStatements.some((statementId) => selectedStatements.has(statementId));
      if (hasSelectedStatement && family?.id != null) {
        nextCheckedIds.add(String(family.id));
      }
    });

    setCheckedNodeIds(nextCheckedIds);
  }, [treeData]);

  const handlePortfolioSelectionChange = useCallback((portfolioIds) => {
    setSelectedPortfolioIds(new Set((portfolioIds || []).map((id) => String(id))));
  }, []);

  const handleGo = useCallback(async () => {
    if (!selectedView) {
      toast.error("Please select a View");
      return;
    }

    const effectiveSelectedPortfolioIds = selectedPortfolios
      .map((p) => getPortfolioId(p))
      .filter((id) => selectedPortfolioIds.has(id));

    if (effectiveSelectedPortfolioIds.length === 0) {
      toast.error("Please select any portfolio");
      return;
    }

    setLoading(true);

    try {
      const groupingCombination = getGroupingCombinationFromView(selectedView);
      const viewId = selectedView?.viewID ?? selectedView?.ViewID ?? 0;
      const reportClassificationId =
        selectedReportClassification?.reportClassificationId
        ?? selectedReportClassification?.ReportClassificationId
        ?? 0;
      const portfolioIds = (effectiveSelectedPortfolioIds.length > 0
        ? effectiveSelectedPortfolioIds
        : selectedPortfolios.map((p) => getPortfolioId(p)).filter(Boolean));
      const runStats = buildRunStatList(portfolioIds, reportDate);
      const endDate = reportDate instanceof Date
        ? reportDate.toISOString()
        : new Date(reportDate).toISOString();

      const response = await getPortfolioAttributionUIData({
        runStats,
        hideZero: false,
        viewId,
        grouping: groupingCombination,
        reportClassificationId,
        endDate,
      });

      const { columns, stats, attrs } = buildGridColumns({
        selectedPortfoliosCount: effectiveSelectedPortfolioIds.length,
        view: selectedView,
        reportDate,
        groupingFields: getGroupingFieldsFromView(selectedView),
      });

      const payload = pick(response, "data", "Data") || response || {};
      const fundList = pick(payload, "fundList", "FundList") || [];
      const statList = pick(payload, "statList", "StatList") || [];
      const otherAttrList = pick(payload, "otherAttrList", "OtherAttrList") || [];

      const rows = fundList.map((fund, fundIndex) => {
        const row = {
          fund: pick(fund, "fundName", "FundName", "assetName", "AssetName") || "-",
          BasketShortName: pick(fund, "basketShortName", "BasketShortName", "portfolioShortName", "PortfolioShortName") || "",
          BillableName: mapBillableDisplayName(pick(fund, "billableName", "BillableName") || ""),
          StrategyName: pick(fund, "strategyName", "StrategyName") || "",
          SubStrategyName: pick(fund, "subStrategyName", "SubStrategyName") || "",
          AssetClassName: pick(fund, "assetClassName", "AssetClassName") || "",
          strategyId: pick(fund, "strategyId", "StrategyId"),
          subStrategyId: pick(fund, "subStrategyId", "SubStrategyId"),
          assetClassId: pick(fund, "assetClassId", "AssetClassId"),
          billableSortOrder: pick(fund, "billableSortOrder", "BillableSortOrder", "billableId", "BillableId"),
          fundId: pick(fund, "fundId", "FundId", "assetId", "AssetId"),
          basketId: pick(fund, "basketId", "BasketId", "portfolioId", "PortfolioId"),
          breakPeriod: pick(fund, "breakPeriod", "BreakPeriod"),
          levelCategory: pick(fund, "levelCategory", "LevelCategory"),
          levelId: pick(fund, "levelId", "LevelId"),
          levelIdentifier: pick(fund, "levelIdentifier", "LevelIdentifier"),
          isPartnership: pick(fund, "isPartnership", "IsPartnership", "isPartnerEntity", "IsPartnerEntity"),
          partnershipId: pick(fund, "partnershipId", "PartnershipId", "BackingBasketId"),
          _rawStats: statList[fundIndex] || [],
          _rawOtherAttrs: otherAttrList[fundIndex] || [],
        };

        stats.forEach((stat, statIndex) => {
          const value = statList[fundIndex]?.[statIndex];
          row[`year${statIndex}`] = value;
        });

        attrs.forEach((attr, attrIndex) => {
          const value = otherAttrList[fundIndex]?.[attrIndex]?.[0];
          row[`${sanitizeKey(attr.keyName ?? attr.KeyName)}_0`] = value;
        });

        return row;
      });

      const filteredRows = rows.filter((row) => shouldIncludeFundByHideZero(row, stats, attrs, hideZero));

      let mappedRows = filteredRows;
      try {
        const portfolioIdLst = portfolioIds.join(",");
        const mappingList = await getPortfolioDocumentMapping(portfolioIdLst, reportDate);
        const mappings = Array.isArray(mappingList) ? mappingList : [];

        // Holdings rows may be aggregated across portfolios; map documents by AssetId so the icon still appears
        // when any selected portfolio has documents for that security.
        const docsByAssetId = new Map();
        for (const mapping of mappings) {
          const assetId = Number(mapping?.AssetId ?? mapping?.assetId) || 0;
          if (!assetId) continue;

          const list = Array.isArray(mapping?.DocumentList)
            ? mapping.DocumentList
            : Array.isArray(mapping?.documentList)
              ? mapping.documentList
              : [];
          if (list.length === 0) continue;

          const existing = docsByAssetId.get(assetId) || [];
          const merged = [...existing, ...list];
          const seen = new Set();
          const unique = merged.filter((doc) => {
            const docId = Number(doc?.DocumentId ?? doc?.documentId) || 0;
            if (!docId) return false;
            if (seen.has(docId)) return false;
            seen.add(docId);
            return true;
          });

          docsByAssetId.set(assetId, unique);
        }

        mappedRows = filteredRows.map((row) => {
          const assetId = Number(pick(row, "fundId", "FundId", "assetId", "AssetId")) || 0;
          const docs = docsByAssetId.get(assetId) || null;
          if (!docs || docs.length === 0) return row;
          return { ...row, document: { AssetId: assetId, DocumentList: docs } };
        });
      } catch {
        // Non-blocking: grid still renders without document metadata.
        mappedRows = filteredRows;
      }

      setGridColumns(columns);
      setGridData(mappedRows);
    } catch (error) {
      console.error("Failed to load holdings grid", error);
      toast.error("Failed to load holdings data.");
    } finally {
      setLoading(false);
    }
  }, [selectedView, selectedPortfolios, selectedPortfolioIds, selectedReportClassification, reportDate, hideZero]);

  const handleViewDocument = useCallback(
    async (doc, _rowData) => {
      const documentId = Number(doc?.DocumentId ?? doc?.documentId) || 0;
      if (!documentId) return;

      const docNameRaw = String(doc?.DocumentName ?? doc?.documentName ?? "").trim() || `Document_${documentId}`;
      const extMatch = docNameRaw.match(/\.([a-z0-9]{1,8})$/i);
      const ext = extMatch ? `.${extMatch[1].toLowerCase()}` : ".pdf";

      const userId = Number(authUserId) || 0;
      const appRoleId = Number(authRoleId) || 0;

      // Open our in-app preview page. Pass userId/roleId in query params because auth store
      // is persisted to localStorage (new tabs don't share it).
      const type = ext === ".pdf" ? "pdf" : ext.replace(/^\./, "");
      const viewerUrl = `/DocumentPreview/${encodeURIComponent(type)}/${documentId}?userId=${encodeURIComponent(
        String(userId)
      )}&appRoleId=${encodeURIComponent(String(appRoleId))}`;
      // Allow access to `window.opener` so the preview tab can hydrate auth state if needed.
      const win = window.open(viewerUrl, "_blank");
      if (win) {
        // Best-effort: send auth details to the new tab without putting the token in the URL.
        const origin = window.location.origin;
        const payload = {
          type: "DOC_PREVIEW_AUTH",
          token: authToken,
          userId,
          appRoleId,
        };
        setTimeout(() => {
          try { win.postMessage(payload, origin); } catch { /* ignore */ }
        }, 200);
        setTimeout(() => {
          try { win.postMessage(payload, origin); } catch { /* ignore */ }
        }, 800);
      }
    },
    [authRoleId, authToken, authUserId]
  );


  const handleExportTimeSeries = useCallback(
    async (exportRequest) => {
      try {
        const reportClassificationId =
          selectedReportClassification?.reportClassificationId
          ?? selectedReportClassification?.ReportClassificationId
          ?? 0;

        const mode = exportRequest?.__exportType || "security";
        const rowData = exportRequest?.rowData || exportRequest?.sampleData || exportRequest || {};

        const selectedPortfolioCsv = selectedPortfolios
          .map((p) => getPortfolioId(p))
          .filter((id) => selectedPortfolioIds.has(id))
          .join(",");

        const primaryPortfolioId = Number(selectedPortfolioCsv.split(",")[0]) || 0;

        let payload;
        if (mode === "portfolio") {
          payload = {
            type: "portfolio",
            asset: "Invested Capital Total",
            assetId: 0,
            portfolioId: primaryPortfolioId,
            breakPeriod: 0,
            levelCategory: 0,
            levelId: 0,
            addHistory: true,
            endDate: reportDate instanceof Date ? reportDate.toISOString() : new Date(reportDate).toISOString(),
            levelIdentifier: selectedPortfolioCsv,
            isPartnership: false,
            partnershipID: 0,
            portfolioIDs: selectedPortfolioCsv,
          };
        } else if (mode === "group") {
          const groupPath = Array.isArray(exportRequest?.groupPath) ? exportRequest.groupPath : [];
          const deepestField = groupPath.length > 0 ? groupPath[groupPath.length - 1].field : "";
          const derivedLevelIdentifier = groupPath
            .map((g) => getLevelIdForField(rowData, g.field))
            .filter((id) => id > 0)
            .join(",");
          const derivedAssetId = Number(
            groupPath.length > 0 ? getLevelIdForField(rowData, deepestField) : 0
          ) || 0;
          const levelCategory = getTimeSeriesLevelCategoryForView(selectedView);
          const levelIdentifier = String(
            pick(rowData, "levelIdentifier", "LevelIdentifier") || derivedLevelIdentifier
          );

          payload = {
            type: "group",
            asset: groupPath.length > 0 ? String(groupPath[groupPath.length - 1].key || "") : "Group",
            assetId: derivedAssetId || Number(pick(rowData, "fundId", "FundId", "assetId", "AssetId")) || 0,
            portfolioId: primaryPortfolioId,
            breakPeriod: 0,
            levelCategory,
            levelId: 0,
            addHistory: true,
            endDate: reportDate instanceof Date ? reportDate.toISOString() : new Date(reportDate).toISOString(),
            levelIdentifier,
            isPartnership: Boolean(pick(rowData, "isPartnership", "IsPartnership", "isPartnerEntity", "IsPartnerEntity")),
            partnershipID: Number(pick(rowData, "partnershipId", "PartnershipId", "BackingBasketId")) || 0,
            portfolioIDs: selectedPortfolioCsv,
          };
        } else {
          payload = {
            type: "security",
            asset: rowData?.fund || "N/A",
            assetId: Number(pick(rowData, "fundId", "FundId", "assetId", "AssetId")) || 0,
            portfolioId: Number(pick(rowData, "basketId", "BasketId", "portfolioId", "PortfolioId")) || 0,
            breakPeriod: Number(pick(rowData, "breakPeriod", "BreakPeriod")) || 0,
            levelCategory: Number(pick(rowData, "levelCategory", "LevelCategory")) || 0,
            levelId: Number(pick(rowData, "levelId", "LevelId")) || 0,
            addHistory: true,
            endDate: reportDate instanceof Date ? reportDate.toISOString() : new Date(reportDate).toISOString(),
            levelIdentifier: String(pick(rowData, "levelIdentifier", "LevelIdentifier") || ""),
            isPartnership: Boolean(pick(rowData, "isPartnership", "IsPartnership", "isPartnerEntity", "IsPartnerEntity")),
            partnershipID: Number(pick(rowData, "partnershipId", "PartnershipId", "BackingBasketId")) || 0,
            portfolioIDs: selectedPortfolioCsv,
          };
        }

        const timeSeriesData = await exportTimeSeries(payload, reportClassificationId);

        if (!Array.isArray(timeSeriesData) || timeSeriesData.length === 0) {
          toast.error("No data available for export.");
          return;
        }

        downloadTimeSeriesExcel(timeSeriesData, payload?.asset);
      } catch (error) {
        console.error("Failed to export time series", error);
        toast.error(`Failed to export time series. ${error?.message || ""}`.trim());
      }
    },
    [selectedReportClassification, reportDate, selectedPortfolios, selectedPortfolioIds, selectedView]
  );

  useEffect(() => {
    if (initialGridLoadedRef.current) {
      return;
    }

    if (!selectedView || !selectedReportClassification) {
      return;
    }

    if (selectedPortfolios.length === 0 || selectedPortfolioIds.size === 0) {
      return;
    }

    initialGridLoadedRef.current = true;
    handleGo();
  }, [selectedView, selectedReportClassification, selectedPortfolios, selectedPortfolioIds, handleGo]);

  return (
    <div className="minHeightDiv" id="AssetDetailsSection">
      <HoldingsFilters
        reportDate={reportDate}
        onReportDateChange={setReportDate}
        reportClassifications={reportClassifications}
        selectedReportClassification={selectedReportClassification}
        onReportClassificationChange={setSelectedReportClassification}
        views={views}
        selectedView={selectedView}
        onViewChange={setSelectedView}
        hideZero={hideZero}
        onHideZeroChange={setHideZero}
        onGo={handleGo}
        additionalControls={(
          <>
          <div className="col-s-8 col-md-4">
            <HoldingsStatementSelector
              label="Statement:"
              emptyText="No statements available"
              placeholder="Select statement(s)"
              singularSummarySuffix="statement selected"
              pluralSummarySuffix="statements selected"
              options={treeData.flatMap((family) =>
                (family?.items || []).map((statement) => ({
                  id: String(statement.id),
                  text: statement.text || "",
                }))
              )}
              selectedIds={Array.from(checkedNodeIds).filter((id) =>
                treeData.some((family) => (family?.items || []).some((statement) => String(statement.id) === id))
              )}
              onSelectionChange={handleStatementSelectionChange}
            />
          </div>
          <div className="col-s-8 col-md-4">
            <HoldingsPortfolioSelector
              selectedPortfolios={selectedPortfolios}
              selectedPortfolioIds={selectedPortfolioIds}
              onSelectionChange={handlePortfolioSelectionChange}
            />
          </div>
          </>
        )}
      />

      <section className="holdings-content-wrap">
        <div className="col-md-12">
          <h3 style={{marginLeft:"20px"}}>Asset Holdings</h3>
          <div id="holdingsViewportRow">
            <div id="holdingsLayout">
              <HoldingsGrid
                data={gridData}
                columns={gridColumns}
                loading={loading}
                onExportTimeSeries={handleExportTimeSeries}
                onViewDocument={handleViewDocument}
                reportDate={reportDate}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AssetHoldingsPage;


